import * as React from 'react';
import {
  FunctionComponent,
  PropsWithChildren,
  ReactElement,
  useEffect,
} from 'react';

import { ThemeProvider } from '@mui/material';
import { withFormik } from 'formik';
import compose from 'lodash/fp/compose';

import StyledFormWrapper from '../../../../shared/components/styled-form-wrapper/styled-form-wrapper';
import { withHigherOrderComponent } from '../../../../shared/components/with-higher-order-component/with-higher-order-component';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import Box from '../../../shared/components/ui/Box';
import { ApkInfoModel } from '../../../shared/models/apk-info.model';
import { formTheme } from '../../../shared/themes/form-theme/form.theme';
import {
  mapFileMetadataModelToIdFile,
  mapStatesEnumToItemLabelModel,
} from '../../../shared/utils/mapping/mapping.utils';
import { useSetApkFiles } from '../../hooks/use-set-apk-files/use-set-apk-files.hook';
import { releasesActions } from '../../store/slices/releases.slice';
import { ReleasesFormProps } from '../ReleasesForm/models/releases-form-props.model';
import { ReleasesFormikModel } from '../ReleasesForm/models/releases-formik.model';
import { AndroidMaxSdkVersionInput } from './components/android-max-sdk-version-input';
import { AndroidMinSdkVersionInput } from './components/android-min-sdk-version-input';
import { AndroidPackageInput } from './components/android-package-input';
import { ApkFilesDragAndDrop } from './components/apk-files-drag-and-drop/apk-files-drag-and-drop';
import ApkPlatformsDropdown from './components/apk-platforms-dropdown/apk-platforms-dropdown';
import ReleaseStatusSelect from './components/apk-platforms-dropdown/components/release-status-select';
import { ReleaseActionButtons } from './components/release-action-buttons';
import { ReleaseDescriptionInput } from './components/release-description-input';
import { VersionCodeInput } from './components/version-code-input';
import { VersionNameInput } from './components/version-name-input';
import withExceptedAppsReleasesForm from './components/with-excepted-apps-releases-form/with-excepted-apps-releases-form';
import { ExceptedAppsReleaseFormikModel } from './models/excepted-apps-release-formik.model';
import { OuterExceptedAppReleasesFormProps } from './models/outer-excepted-app-releases-form.props';
import { exceptedAppsReleaseFormSchema } from './schema/excepted-apps-release-form.schema';
import { ExceptedAppsReleaseFormProps } from './types/excepted-apps-release-form-props.type';

export const ExceptedAppsReleaseForm: FunctionComponent<
  ExceptedAppsReleaseFormProps
> = (props: PropsWithChildren<ExceptedAppsReleaseFormProps>): ReactElement => {
  const { children, handleSubmit, values, data } = props;
  const dispatch = useAppDispatch();

  const apkPlatforms = useAppSelector(
    state => state.appsFeature.releases.platforms
  );

  useSetApkFiles(data);

  useEffect(() => {
    const apkInfoValues: ApkInfoModel = {
      versionName: values.versionName,
      versionCode: values.versionCode as number,
      androidMinSDKVersion: values.androidMinSDKVersion as number,
      androidMaxSDKVersion: values.androidMaxSDKVersion as number,
      androidPackage: values.androidPackage,
      platforms: apkPlatforms.filter(item =>
        values.platforms.includes(item.id)
      ),
    };
    dispatch(releasesActions.setApkInfoValues(apkInfoValues));
  }, [values]);

  return (
    <ThemeProvider theme={formTheme}>
      <Box
        sx={{
          my: '48px',
        }}
      >
        <StyledFormWrapper onSubmit={handleSubmit}>
          {children}
        </StyledFormWrapper>
      </Box>
    </ThemeProvider>
  );
};

const WrappedExceptedAppsReleaseForm = withFormik<
  OuterExceptedAppReleasesFormProps & ReleasesFormProps,
  ExceptedAppsReleaseFormikModel
>({
  mapPropsToValues: props => {
    return {
      versionName: props.data?.versionName || '',
      versionCode: props.data?.versionCode || '',
      androidMaxSDKVersion: props.data?.androidMaxSDKVersion || '',
      androidMinSDKVersion: props.data?.androidMinSDKVersion || '',
      androidPackage: props.data?.androidPackage || '',
      platforms: props.data?.platforms.map(p => p.id) || [],
      apkFiles: props.appsFiles?.map(mapFileMetadataModelToIdFile) ?? [],
      description: props.data?.description || '',
      status: props.data
        ? mapStatesEnumToItemLabelModel(props.data.status)
        : null,
    };
  },
  validationSchema: exceptedAppsReleaseFormSchema(),
  handleSubmit: (values, formikBag) => {
    const releaseValues: Partial<ReleasesFormikModel> = {
      status: values.status,
      description: values.description,
      defaultApkFiles: formikBag.props.appsFiles.map(
        mapFileMetadataModelToIdFile
      ),
    };

    return formikBag.props.onSubmitReleaseForm(releaseValues);
  },
  validateOnChange: true,
  validateOnBlur: true,
  validateOnMount: false,
})(
  compose(
    withHigherOrderComponent(VersionNameInput),
    withHigherOrderComponent(VersionCodeInput),
    withHigherOrderComponent(AndroidMinSdkVersionInput),
    withHigherOrderComponent(AndroidMaxSdkVersionInput),
    withHigherOrderComponent(AndroidPackageInput),
    withHigherOrderComponent(ApkPlatformsDropdown),
    withHigherOrderComponent(ApkFilesDragAndDrop),
    withHigherOrderComponent(ReleaseDescriptionInput),
    withHigherOrderComponent(ReleaseStatusSelect),
    withHigherOrderComponent(ReleaseActionButtons)
  )(ExceptedAppsReleaseForm)
);

const ExceptedAppsReleaseFormWrapped = withExceptedAppsReleasesForm(
  WrappedExceptedAppsReleaseForm
);

export default ExceptedAppsReleaseFormWrapped;

export { ExceptedAppsReleaseFormWrapped };
